import { z } from 'zod/v4';
import { SensitivityFlag } from '../ai/index.js';
import { CaseMergeRulesSchema } from '../customers/index.js';
import { CaseVoucherTableConfigSchema, } from '../economy/case-vouchers/tables.js';
import { PaymentPlansConfigSchema, } from '../economy/payment-plans/index.js';
import { AddressSchema, BankAccountType, CountryCode, Currency, Timezone, } from '../index.js';
import { Lang } from '../lang/Lang.js';
import { PerformedBySchema } from '../performed-by/index.js';
import { DefaultProceduresSchema, } from '../procedures/index.js';
import { TribunalCaseType } from '../tribunal/cases/types.js';
export var TenantType;
(function (TenantType) {
    TenantType["Agency"] = "AGENCY";
    TenantType["AccountsReceivable"] = "ACCOUNTS_RECEIVABLE";
})(TenantType || (TenantType = {}));
export var ReferenceIdType;
(function (ReferenceIdType) {
    ReferenceIdType["CustomerReferenceId"] = "CUSTOMER_REFERENCE_ID";
    ReferenceIdType["CaseVoucherReferenceId"] = "CASE_VOUCHER_REFERENCE_ID";
})(ReferenceIdType || (ReferenceIdType = {}));
const TribunalSettingsSchema = z.object({
    courtRepresentative: z.string().optional(),
    courtPhone: z.string().optional(),
    courtEmail: z.string().optional(),
    courtRegistrationNumber: z.string().optional(),
    courtAccountNumber: z.string().optional(),
    caseTypes: z
        .object({
        [TribunalCaseType.DKBetalingspaakrav]: z
            .object({
            courtCostVoucherTypeId: z.uuid().optional(),
            insolvencyUpdateTypeId: z.uuid().optional(),
            debtorUserOnCustomerIds: z.array(z.uuid()).optional(),
            overrideVoucherTypeIds: z.array(z.uuid()).optional(),
            subtractVoucherTypeIdsFromCourtCost: z.array(z.uuid()).optional(),
            addInterestOnVoucherTypeIdsAfterVerdict: z.array(z.uuid()).optional(),
            excludeVoucherTypeIds: z.array(z.uuid()).optional(),
            maximizeCollectionCost: z.boolean().optional(),
            ignoreWarningThatCaseHasDeposits: z.boolean().optional(),
            rubrik1Creditor: z.string().optional(),
            rubrik1Representative: z.string().optional(),
            rubrik1ContactInformation: z.string().optional(),
            showCPRInBpk: z.boolean().optional(),
            sagsfremstilling: z.string().nullable().optional(),
            noPhoneMeetingBelowAmount: z.number().nullable().optional(),
        })
            .optional(),
        [TribunalCaseType.DKFogedrekvisition]: z
            .object({
            defaultEmailTemplateId: z.uuid().nullable().optional(),
            defaultEmailAttachmentTemplateId: z.uuid().nullable().optional(),
        })
            .optional(),
        [TribunalCaseType.DKAndreFogedsager]: z
            .object({
            defaultEmailTemplateId: z.uuid().nullable().optional(),
            defaultEmailAttachmentTemplateId: z.uuid().nullable().optional(),
        })
            .optional(),
        [TribunalCaseType.GLBetalingspaakrav]: z
            .object({
            sagsfremstilling: z.string().nullable().optional(),
        })
            .optional(),
        [TribunalCaseType.GLFogedBegaering]: z
            .object({
            sagsfremstilling: z.string().nullable().optional(),
        })
            .optional(),
        [TribunalCaseType.GLStaevning]: z
            .object({
            sagsfremstilling: z.string().nullable().optional(),
        })
            .optional(),
    })
        .optional(),
});
export const BillingTextFieldSettingsSchema = z.object({
    underCreditorName: z.string().nullish(),
    endWhenReceivingPayment: z.string().nullish(),
    endWhenSendingPayment: z.string().nullish(),
});
export const ssoDefaultRoles = ['caseworker'];
export const ssoRoleManagement = (tenant) => tenant.authSettings?.sso?.roleManagement ?? 'idp';
export var TenantStatus;
(function (TenantStatus) {
    TenantStatus["GettingStarted"] = "GETTING_STARTED";
    TenantStatus["Demo"] = "DEMO";
    TenantStatus["Active"] = "ACTIVE";
    TenantStatus["Sandbox"] = "SANDBOX";
    TenantStatus["Disabled"] = "DISABLED";
})(TenantStatus || (TenantStatus = {}));
export var ErpVendorType;
(function (ErpVendorType) {
    ErpVendorType["EConomic"] = "E_CONOMIC";
})(ErpVendorType || (ErpVendorType = {}));
export const TenantStatusSchema = z.enum(TenantStatus);
export const PriceListSchema = z.object({
    caseCloseFee: z.number().int().nullish(),
    clientCaseCreationFee: z.number().int().nullish(),
    tenantCaseCreationFee: z.number().int().nullish(),
    debtRegisterRegistrationFee: z.number().int().nullish(),
    paymentDemandFee: z.number().int().nullish(),
    fogedrekvisitionsFee: z.number().int().nullish(),
    folkeregisteropslagsFee: z.number().int().nullish(),
    anmeldelseAfKravFee: z.number().int().nullish(),
    legalAdviceHourlyRate: z.number().int().nullish(),
    afslysningAfUdlaeg: z.number().int().nullish(),
});
export const AKSSettingsSchema = z.object({
    key: z.string().optional(),
    code: z.string().optional(),
    password: z.string().optional(),
    username: z.string().optional(),
    limit: z.number().optional(),
    enabled: z.boolean().optional(),
});
export const DebitorRegistretSettingsSchema = z.union([
    z.object({
        clientId: z.string(),
        clientSecret: z.string(),
        enabled: z.boolean().optional(),
    }),
    z.object({
        enabled: z.boolean().optional(),
    }),
]);
export const ServerSetupSchema = z.object({
    type: z.enum(['FTP']),
    data: z.object({
        user: z.string(),
        host: z.string(),
        password: z.string(),
        port: z.number(),
    }),
});
export const AuthSettingsSchema = z.object({
    sso: z.object({
        enabled: z.boolean(),
    }),
});
export const CustomSensitivityFlagSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
});
export const AutoReplySettingsSchema = z.object({
    enabled: z.boolean(),
    mode: z.enum(['outbox', 'auto-send']),
    qualityThreshold: z.number().min(0).max(1).default(0.8),
    safetyThreshold: z.number().min(0).max(1).default(0.7),
    blocklist: z.array(z.enum(SensitivityFlag)).default([]),
    customSensitivityFlags: z.array(CustomSensitivityFlagSchema).optional(),
    generationModel: z.string().optional(),
    judgeModel: z.string().optional(),
});
export const CategoryDiscoverySettingsSchema = z.object({
    enabled: z.boolean(),
});
export const AISettingsSchema = z.object({
    customInstructions: z.string().nullish(),
    autoReply: AutoReplySettingsSchema.optional(),
    categoryDiscovery: CategoryDiscoverySettingsSchema.optional(),
});
export const AICreditsConfigSchema = z.object({
    // Whole credits — the usage gate converts with BigInt(), which throws on fractions
    monthlyAllowance: z.number().int().min(0).default(10),
});
// Must match the schema default above
export const DEFAULT_AI_CREDITS = {
    monthlyAllowance: 10,
};
export const SettingsSchema = z.object({
    autoImposeCompensationCost: z.boolean().nullish(),
    autoSendInteractionsAfterXVerifications: z
        .number()
        .min(0)
        .max(9999)
        .nullish(),
    preventSendingLetters: z.boolean().nullish(),
    preventAttachingBillingsToBillingEmails: z.boolean().nullish(),
    disableWorkflows: z.boolean().nullish(),
    paymentPlanFoundationTemplateId: z.uuid().nullish(),
    disablePaymentPlanSigning: z.boolean().nullish(),
    selfCollection: z.boolean().nullish(),
    twoFactorRequired: z.boolean().nullish(),
    aiDisabled: z
        .boolean()
        .nullish()
        .transform((x) => x ?? undefined),
    legisIntegration: z
        .boolean()
        .nullish()
        .transform((x) => x ?? undefined),
    outboundCall: z.boolean().nullish(),
    autoDebtRegister: z.boolean().nullish(),
    vatConnectorId: z.uuid().nullish(),
    autoVerifyCases: z.boolean().nullish(),
    hideDocumentsFromCreditors: z.boolean().nullish(),
    allowDebtorSelfOverwrite: z.boolean().nullish(),
    advokatindestaaelse: z.boolean().nullish(),
    autoRegisterInOpenRegistry: z.boolean().nullish(),
    autoIgnoreUnresolvedOppositions: z.boolean().nullish(),
    autoDeleteDebtRegistrationOnCaseEnd: z.boolean().nullish(),
    manglerAdvokatindestaaelse: z.boolean().nullish(),
    gdpr: z
        .object({
        deleteCaseDataAfterYears: z.number().min(1).max(99).nullish(),
    })
        .nullish(),
    responsibleForUpdateTypes: z.any().nullish(),
    accountingExportSettings: z.any().nullish(),
    letterSetup: z
        .object({
        returnAddress: AddressSchema,
        server: ServerSetupSchema.optional(),
    })
        .nullish(),
    referenceIdFinders: z
        .array(z.object({
        referenceIdType: z.enum(ReferenceIdType),
        referenceIdLength: z.number(),
        referenceIdMin: z.number().nullish(),
        referenceIdMax: z.number().nullish(),
    }))
        .optional(),
    tribunal: TribunalSettingsSchema.optional(),
    debtorweb: z
        .object({
        contactUsText: z.string().optional(),
        welcomeText: z.string().optional(),
        hideContactInformation: z.boolean().optional(),
        noPartialPayments: z.boolean().optional(),
        doNotEncourageAPaymentPlan: z.boolean().optional(),
        preferMigratedJrNr: z.boolean().optional(),
    })
        .optional(),
    billingTextFields: z
        .object({
        underCreditorName: z.string().nullish(),
        endWhenReceivingPayment: z.string().nullish(),
        endWhenSendingPayment: z.string().nullish(),
    })
        .nullish()
        .transform((x) => x ?? undefined),
    workflows: z
        .object({
        executionInterval: z
            .object({
            fromHour: z
                .number()
                .int()
                .nullish()
                .transform((x) => x ?? undefined),
            fromMinute: z
                .number()
                .int()
                .nullish()
                .transform((x) => x ?? undefined),
            toHour: z
                .number()
                .int()
                .nullish()
                .transform((x) => x ?? undefined),
            toMinute: z
                .number()
                .int()
                .nullish()
                .transform((x) => x ?? undefined),
        })
            .optional(),
    })
        .optional(),
    customCaseVoucherTables: z.array(CaseVoucherTableConfigSchema).optional(),
    documentSigningLinks: z.any().optional(),
    userOverrides: z
        .array(z.object({
        key: z.string(),
        match: z.object({
            cvr: z
                .string()
                .nullish()
                .transform((x) => x ?? undefined),
        }),
        set: z.object({
            userEmail: z.string().nullish(),
            customerTag: z
                .string()
                .nullish()
                .transform((x) => x ?? undefined),
        }),
    }))
        .optional(),
    ai: AISettingsSchema.optional(),
    costCeiling: z
        .object({
        enabled: z.boolean().optional(),
    })
        .optional(),
    aiCredits: AICreditsConfigSchema.optional(),
});
const TenantBillingEmailsSchema = z.object({
    to: z.string().nullish(),
    cc: z.string().nullish(),
    bcc: z.string().nullish(),
});
export const UpdateSchema = z.object({
    name: z.string().optional(),
    type: z.enum(TenantType).optional(),
    creditorId: z.uuid().optional(),
    countryCode: z.string().optional(),
    currency: z.string().optional(),
    collectorIdentityId: z.uuid().optional(),
    defaultWorkflowId: z.uuid().nullish(),
    defaultBillingModelId: z.uuid().nullish(),
    defaultBillingAppendixSetupId: z.uuid().nullish(),
    status: TenantStatusSchema.optional(),
    economicToken: z.string().optional(),
    address: AddressSchema.nullish(),
    priceList: PriceListSchema.optional(),
    settings: SettingsSchema.optional(),
    embeds: z.any().optional(),
    defaultProcedures: DefaultProceduresSchema.nullish(),
    billingPriceList: z.any().optional(),
    billingEnabled: z.boolean().optional(),
    referenceId: z.string().optional(),
    vatNumber: z.string().optional(),
    paymentTerms: z.any().optional(),
    invoicingEmails: TenantBillingEmailsSchema.optional(),
    caseMergeRules: CaseMergeRulesSchema.nullish(),
    paymentPlansConfig: PaymentPlansConfigSchema.nullish(),
    credits: z.any().optional(),
    aksSettings: AKSSettingsSchema.optional(),
    debitorRegistretSettings: DebitorRegistretSettingsSchema.optional(),
    caseGroupWorkflows: z.any().nullish(),
    bankAccountDetails: z
        .object({
        type: z.enum(BankAccountType),
        bankName: z.string(),
        registrationNumber: z.string(),
        accountNumber: z.string(),
        createdAt: z.string(),
    })
        .nullish(),
    erpVendor: z
        .object({
        type: z.enum(ErpVendorType),
        token: z.string(),
    })
        .nullish(),
    featureFlags: z.any().optional(),
});
export const CreateSchema = z.object({
    featureFlags: z.record(z.string(), z.boolean()).optional(),
    type: z.enum(TenantType),
    creditorId: z
        .string()
        .uuid()
        .nullish()
        .transform((x) => x ?? undefined),
    id: z.uuid().optional(),
    name: z.string(),
    address: AddressSchema.nullish(),
    referenceId: z.string().nullish(),
    vatNumber: z.string().nullish(),
    countryCode: z.enum(CountryCode),
    currency: z.enum(Currency),
    lang: z.enum(Lang),
    timezone: z.enum(Timezone),
    collectorIdentityId: z.uuid(),
    defaultWorkflowId: z.uuid().optional(),
    defaultBillingModelId: z.uuid().nullish(),
    defaultBillingAppendixSetupId: z.uuid().nullish(),
    status: TenantStatusSchema.optional(),
    economicToken: z.string().optional(),
    createdBy: PerformedBySchema.nullish(),
    defaultProcedures: DefaultProceduresSchema.nullish(),
    settings: SettingsSchema.optional(),
    authSettings: AuthSettingsSchema.optional(),
});
export var TenantMockDataType;
(function (TenantMockDataType) {
    TenantMockDataType["PreCollection"] = "PRE_COLLECTION";
    TenantMockDataType["DebtCollection"] = "DEBT_COLLECTION";
})(TenantMockDataType || (TenantMockDataType = {}));
//# sourceMappingURL=index.js.map